import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Download, ExternalLink, FileText } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Shows every document of one type linked to an OA (annexure by oaId, quotation/bill by oaNumber),
// plus the OA's PO file for docType="po". Original files are preserved and streamed as-is.
export default function OADocuments({ docType, oaId, oaNumber, customerName, poFile }) {
  const { user } = useAuth();
  const can = (p) => user.role === "admin" || (user.permissions || []).includes(p);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const canUpload = docType !== "po" && can(`upload_${docType}`);
  const canDownload = docType === "po" ? can("download_po") : can(`download_${docType}`);

  const load = useCallback(async () => {
    if (docType === "po") return;
    try {
      const params = docType === "annexure" ? { oaId } : { oaNumber };
      const { data } = await api.get(`/documents/${docType}`, { params });
      setDocs(data || []);
    } catch { setDocs([]); }
  }, [docType, oaId, oaNumber]);
  useEffect(() => { load(); }, [load]);

  const openDoc = (d, mode) => {
    api.get(`/documents/${docType}/${d.id}/file`, { params: { mode }, responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        if (mode === "download") { const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click(); }
        else window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  const openPo = (mode) => {
    api.get("/po/download", { params: { path: poFile.storagePath, mode }, responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        if (mode === "download") { const a = document.createElement("a"); a.href = url; a.download = poFile.filename; a.click(); }
        else window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/documents/${docType}/upload`, fd, {
        params: { oaId, oaNumber, customerName },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded and linked to " + oaNumber);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (docType === "po") {
    return (
      <div className="card p-6 max-w-2xl" data-testid="oa-docs-po">
        {poFile ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-8 w-8 text-amber-600 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{poFile.filename}</div>
                <div className="text-xs text-slate-400">Purchase Order · original file preserved</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button className="btn-outline btn-sm" onClick={() => openPo("view")} data-testid="po-view-btn"><ExternalLink className="h-4 w-4" /> View</button>
              {canDownload && <button className="btn-outline btn-sm" onClick={() => openPo("download")} data-testid="po-download-btn"><Download className="h-4 w-4" /> Download</button>}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-400" data-testid="po-empty">No data found for this O.A. Number. PO files are managed from the OA edit page.</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid={`oa-docs-${docType}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{docs.length} document(s) linked to <span className="font-mono font-semibold text-slate-800">{oaNumber}</span></div>
        {canUpload && (
          <label className="btn-accent btn-sm cursor-pointer" data-testid={`${docType}-oa-upload`}>
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload & Link"}
            <input type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          </label>
        )}
      </div>
      {docs.length === 0 ? (
        <div className="card p-8 text-center text-sm text-slate-400" data-testid={`${docType}-oa-empty`}>
          No data found for this O.A. Number. Add new data.
        </div>
      ) : (
        <div className="card divide-y divide-slate-100">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`${docType}-oa-doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{d.filename}</div>
                  <div className="text-xs text-slate-400">{d.fileType}{d.size ? ` · ${Math.round(d.size / 1024)} KB` : ""} · {d.uploadedByName || d.uploadedBy} · {(d.uploadDate || "").slice(0, 10)}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button className="p-1.5 rounded hover:bg-slate-100" title="View" onClick={() => openDoc(d, "view")} data-testid={`${docType}-oa-view-${d.id}`}><ExternalLink className="h-4 w-4 text-slate-600" /></button>
                {canDownload && <button className="p-1.5 rounded hover:bg-slate-100" title="Download original" onClick={() => openDoc(d, "download")} data-testid={`${docType}-oa-dl-${d.id}`}><Download className="h-4 w-4 text-slate-600" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
